import _ from 'lodash';
import React from 'react';
import cn from 'classnames';
import { SelectionSingleType } from 'client/components/common/SelectionHint/types';
import css from './AlignmentGuides.scss';

export const INTERSECTION_SIDE = {
	start: 'start',
	middle: 'middle',
	end: 'end',
};

class Box {
	x: number;

	y: number;

	top: number;

	left: number;

	bottom: number;

	right: number;

	width: number;

	height: number;

	element: HTMLElement;

	isSelected?: boolean;

	constructor(element) {
		this.element = element;

		const { bbox } = this;
		this.x = bbox.x;
		this.y = bbox.y;
		this.top = this.y;
		this.left = this.x;
		this.bottom = bbox.bottom;
		this.right = bbox.right;
		this.width = bbox.width;
		this.height = bbox.height;
	}

	get bbox() {
		return this.element.getBoundingClientRect();
	}

	get interestPoints() {
		return {
			x: [this.x, this.x + this.width / 2, this.right],
			y: [this.y, this.y + this.height / 2, this.bottom],
		};
	}
}

function checkIntersection(selectedBox, interestPoint) {
	let result: null | {} = null;
	const tolerance = 4;

	const [start, middle, end] = selectedBox.interestPoints[interestPoint.axis];

	const diff = {
		[INTERSECTION_SIDE.start]: start - interestPoint.value,
		[INTERSECTION_SIDE.middle]: middle - interestPoint.value,
		[INTERSECTION_SIDE.end]: end - interestPoint.value,
	};

	const isStart = Math.abs(diff[INTERSECTION_SIDE.start]) <= tolerance;
	const isMid = Math.abs(diff[INTERSECTION_SIDE.middle]) <= tolerance;
	const isEnd = Math.abs(diff[INTERSECTION_SIDE.end]) <= tolerance;

	if (isStart || isMid || isEnd) {
		result = {
			[INTERSECTION_SIDE.start]: isStart,
			[INTERSECTION_SIDE.middle]: isMid,
			[INTERSECTION_SIDE.end]: isEnd,
		};
	}

	return {
		isSnap: Object.values(diff).some(value => Math.abs(value) < 1),
		tolerance,
		result,
		diff,
	};
}

function getBoxes(id) {
	const selected = document.getElementById(id) as HTMLElement;
	if (!selected) {
		return {};
	}

	let selectedBox;
	const parent = selected.parentNode;
	// @ts-expect-error ts-migrate FIXME
	const boxes = _.map([parent, ...parent.children], element => {
		const box = new Box(element);
		if (element === selected) {
			selectedBox = box;
			box.isSelected = true;
		}
		return box;
	});

	return {
		boxes,
		selectedBox,
	};
}

type GuideLineProps = {
	axis: 'x' | 'y';
	value: number;
	intersection: {
		result?: {
			start: boolean;
			middle: boolean;
			end: boolean;
		};
		isSnap: boolean;
	};
};

const GuideLine = ({ value, intersection, axis }: GuideLineProps) => (
	<div
		className={cn(css.guideLine, {
			[css.x]: axis === 'x',
			[css.y]: axis === 'y',
			[css.hasIntersection]: !!intersection.result,
			[css.isSnap]: !!intersection.isSnap,
		})}
		style={{
			[axis === 'x' ? 'left' : 'top']: intersection.result && intersection.result.end ? value - 1 : value,
		}}
	/>
);

type AlignmentGuidesProps = {
	debug?: boolean;
	onIntersection: (...args: any[]) => any;
	selection: SelectionSingleType;
};

const AlignmentGuides = (props: AlignmentGuidesProps) => {
	const { boxes, selectedBox } = getBoxes(props.selection.id);
	return (
		<div className={css.guides}>
			{boxes.map(box => {
				const { isSelected, interestPoints } = box;

				if (isSelected) {
					return null;
				}

				return Object.keys(interestPoints).map(axis =>
					interestPoints[axis].map((value, index) => {
						const interestPoint = { axis, value, id: `${box.element.id}-${axis}-${index}` };
						const intersection = checkIntersection(selectedBox, interestPoint);
						if (intersection.result) {
							props.onIntersection(intersection, interestPoint);
						}

						if (!props.debug && intersection.isSnap !== true) {
							return null;
						}

						return (
							// @ts-expect-error ts-migrate FIXME
							<GuideLine key={interestPoint.id} value={value} intersection={intersection} axis={axis} />
						);
					})
				);
			})}
		</div>
	);
};

AlignmentGuides.defaultProps = {
	debug: false,
};

export default AlignmentGuides;
